/**
 * @fileoverview Singer Event Card component.
 * @summary Displays a single event card for singer's event history.
 *
 * @package KaraoNow
 * @module apps/front/src/pages/singer/components/SingerEventCard
 * @author Pierre-Yves Léglise <contact@axialdata.net>
 * @license EULA
 */
import { useTranslation } from 'react-i18next'

interface SingerEvent {
  id: string
  name: string
  organizerName: string
  date: string
  status: 'draft' | 'active' | 'completed' | 'cancelled'
  songsCount: number
}

interface SingerEventCardProps {
  event: SingerEvent
  getStatusColor: (status: string) => string
  formatDate: (dateString: string) => string
  onJoin: (eventId: string) => void
}

/**
 * @summary Singer event card component.
 * @param {SingerEventCardProps} props - Component props.
 * @returns {React.ReactElement} Event card.
 */
export function SingerEventCard({
  event,
  getStatusColor,
  formatDate,
  onJoin,
}: SingerEventCardProps) {
  const { t } = useTranslation()

  return (
    <div className="card bg-base-100 dark:bg-base-800 shadow-sm hover:shadow-md transition-shadow">
      <div className="card-body">
        {/* Header: Title and Status Badge */}
        <div className="flex items-start justify-between gap-3 mb-3">
          <h3 className="card-title text-lg sm:text-xl mb-0">{event.name}</h3>
          <div className={`badge ${getStatusColor(event.status)} whitespace-nowrap`}>
            {t(`singer.status_${event.status}`)}
          </div>
        </div>

        {/* Event Details */}
        <div className="grid grid-cols-1 gap-2 text-sm mb-2">
          <div className="flex items-center gap-2">
            <span className="text-base-600 dark:text-base-400">{t('singer.organizer')}:</span>
            <span className="font-medium text-base-900 dark:text-base-100">{event.organizerName}</span>
          </div>
          <div className="flex items-center gap-2">
            <span className="text-base-600 dark:text-base-400">{t('singer.date')}:</span>
            <span className="font-medium text-base-900 dark:text-base-100">{formatDate(event.date)}</span>
          </div>
          <div className="flex items-center gap-2">
            <span className="text-base-600 dark:text-base-400">{t('singer.songsSung')}:</span>
            <span className="font-medium text-base-900 dark:text-base-100">{event.songsCount}</span>
          </div>
        </div>

        {/* Action Button - Hide if event is completed or cancelled */}
        {event.status !== 'completed' && event.status !== 'cancelled' && (
          <div className="flex gap-2 pt-2 border-t border-base-200 dark:border-base-700">
            <button
              onClick={() => onJoin(event.id)}
              className="btn btn-sm btn-primary flex-1"
            >
              {t('singer.joinEvent')}
            </button>
          </div>
        )}
      </div>
    </div>
  )
}

